import type { TransformOperation, TransformStep } from '../../api/datasets';

export interface StepField {
  key: string;
  label: string;
  kind: 'column' | 'columns' | 'text' | 'number' | 'select' | 'expression';
  /** Options for a select field. */
  options?: { value: string; label: string }[];
  optional?: boolean;
  hint?: string;
}

export interface StepDefinition {
  op: TransformOperation;
  label: string;
  /** What this step does, in the user's terms. */
  description: string;
  fields: StepField[];
  /** One line describing a configured step, for the recipe list. */
  summarise: (step: TransformStep) => string;
}

const TYPE_OPTIONS = [
  { value: 'integer', label: 'Whole number' },
  { value: 'float', label: 'Decimal number' },
  { value: 'boolean', label: 'Yes / no' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date and time' },
  { value: 'text', label: 'Text' },
];

const COMPARISONS = [
  { value: 'eq', label: 'is' },
  { value: 'ne', label: 'is not' },
  { value: 'gt', label: 'is greater than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is at most' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
];

const FILL_STRATEGIES = [
  { value: 'value', label: 'a value I choose' },
  { value: 'zero', label: 'zero' },
  { value: 'mean', label: 'the average' },
  { value: 'median', label: 'the median' },
  { value: 'mode', label: 'the most common value' },
  { value: 'previous', label: 'the value above' },
  { value: 'next', label: 'the value below' },
];

const AGGREGATIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'count', label: 'Count' },
  { value: 'count_distinct', label: 'Distinct count' },
  { value: 'median', label: 'Median' },
];

const text = (step: TransformStep, key: string): string => String(step[key] ?? '…');

/**
 * The operations a user can add, in the order the specification lists them.
 *
 * This catalogue drives the whole builder: every form is generated from it, so
 * the UI can only ever produce steps the backend knows. The backend validates
 * them again regardless (Rule 7).
 */
export const STEP_CATALOG: StepDefinition[] = [
  {
    op: 'rename',
    label: 'Rename a column',
    description: 'Give a column a clearer name.',
    fields: [
      { key: 'column', label: 'Column', kind: 'column' },
      { key: 'to', label: 'New name', kind: 'text' },
    ],
    summarise: (step) => `Rename ${text(step, 'column')} to ${text(step, 'to')}`,
  },
  {
    op: 'change_type',
    label: 'Change a type',
    description: 'Read a column as a number, a date, or text.',
    fields: [
      { key: 'column', label: 'Column', kind: 'column' },
      { key: 'to_type', label: 'Read it as', kind: 'select', options: TYPE_OPTIONS },
    ],
    summarise: (step) => `Read ${text(step, 'column')} as ${text(step, 'to_type')}`,
  },
  {
    op: 'drop_columns',
    label: 'Delete columns',
    description: 'Remove columns you do not need.',
    fields: [{ key: 'columns', label: 'Columns', kind: 'columns' }],
    summarise: (step) => `Delete ${(step.columns as string[])?.join(', ') || '…'}`,
  },
  {
    op: 'filter',
    label: 'Filter rows',
    description: 'Keep only the rows that match.',
    fields: [
      { key: 'column', label: 'Column', kind: 'column' },
      { key: 'operator', label: 'Comparison', kind: 'select', options: COMPARISONS },
      { key: 'value', label: 'Value', kind: 'text', optional: true },
    ],
    summarise: (step) =>
      `Keep rows where ${text(step, 'column')} ${
        COMPARISONS.find((item) => item.value === step.operator)?.label ?? '…'
      } ${step.value === undefined ? '' : String(step.value)}`.trim(),
  },
  {
    op: 'drop_nulls',
    label: 'Remove empty values',
    description: 'Drop rows that have no value in the chosen columns.',
    fields: [{ key: 'columns', label: 'Columns', kind: 'columns', optional: true }],
    summarise: (step) =>
      `Remove rows with no ${(step.columns as string[])?.join(', ') || 'value anywhere'}`,
  },
  {
    op: 'fill_nulls',
    label: 'Fill empty values',
    description: 'Replace empty cells with something.',
    fields: [
      { key: 'column', label: 'Column', kind: 'column' },
      { key: 'strategy', label: 'Fill with', kind: 'select', options: FILL_STRATEGIES },
      { key: 'value', label: 'Value', kind: 'text', optional: true },
    ],
    summarise: (step) =>
      `Fill empty ${text(step, 'column')} with ${
        FILL_STRATEGIES.find((item) => item.value === step.strategy)?.label ?? '…'
      }`,
  },
  {
    op: 'replace_values',
    label: 'Replace values',
    description: 'Swap one value for another.',
    fields: [
      { key: 'column', label: 'Column', kind: 'column' },
      { key: 'find', label: 'Find', kind: 'text' },
      { key: 'replace', label: 'Replace with', kind: 'text', optional: true },
    ],
    summarise: (step) =>
      `In ${text(step, 'column')}, replace ${text(step, 'find')} with ${text(step, 'replace')}`,
  },
  {
    op: 'drop_duplicates',
    label: 'Remove duplicates',
    description: 'Keep only the first of each repeated row.',
    fields: [{ key: 'columns', label: 'Compare by', kind: 'columns', optional: true }],
    summarise: (step) =>
      `Remove duplicates${
        (step.columns as string[])?.length ? ` by ${(step.columns as string[]).join(', ')}` : ''
      }`,
  },
  {
    op: 'calculated_column',
    label: 'Add a calculated column',
    description: 'Build a new column from the ones you have.',
    fields: [
      { key: 'name', label: 'New column name', kind: 'text' },
      {
        key: 'expression',
        label: 'Formula',
        kind: 'expression',
        hint: 'Use columns, numbers and functions: price * units, round(total * 1.16, 2), if_else(units > 10, "bulk", "single").',
      },
    ],
    summarise: (step) => `${text(step, 'name')} = ${text(step, 'expression')}`,
  },
  {
    op: 'group_by',
    label: 'Group and summarise',
    description: 'Collapse rows into one per group, with totals.',
    fields: [
      { key: 'dimensions', label: 'Group by', kind: 'columns', optional: true },
      { key: 'metric_column', label: 'Measure', kind: 'column' },
      {
        key: 'metric_aggregation',
        label: 'Calculate',
        kind: 'select',
        options: AGGREGATIONS,
      },
      { key: 'metric_alias', label: 'Call it', kind: 'text', optional: true },
    ],
    summarise: (step) => {
      const dimensions = (step.dimensions as string[])?.join(', ');
      const metrics = step.metrics as { aggregation: string; column: string }[] | undefined;
      const measure = metrics?.[0];
      return `${measure?.aggregation ?? '…'} of ${measure?.column ?? '…'}${
        dimensions ? ` by ${dimensions}` : ' overall'
      }`;
    },
  },
  {
    op: 'sort',
    label: 'Sort rows',
    description: 'Put the rows in order.',
    fields: [
      { key: 'sort_column', label: 'Sort by', kind: 'column' },
      {
        key: 'sort_direction',
        label: 'Direction',
        kind: 'select',
        options: [
          { value: 'asc', label: 'Smallest first' },
          { value: 'desc', label: 'Largest first' },
        ],
      },
    ],
    summarise: (step) => {
      const by = step.by as { column: string; direction?: string }[] | undefined;
      return `Sort by ${by?.[0]?.column ?? '…'} (${by?.[0]?.direction ?? 'asc'})`;
    },
  },
  {
    op: 'join',
    label: 'Join another dataset',
    description: 'Bring columns from a second dataset in, matched on a shared column.',
    // This step has its own form: its choices depend on each other.
    fields: [],
    summarise: (step) => {
      const on = step.on as { left: string; right: string }[] | undefined;
      const how = step.how === 'left' ? 'Left join' : 'Join';
      return `${how} on ${on?.[0]?.left ?? '…'} = ${on?.[0]?.right ?? '…'}`;
    },
  },
  {
    op: 'limit',
    label: 'Keep the first rows',
    description: 'Cut the dataset down to a number of rows.',
    fields: [{ key: 'rows', label: 'How many rows', kind: 'number' }],
    summarise: (step) => `Keep the first ${text(step, 'rows')} rows`,
  },
];

export const stepDefinition = (op: TransformOperation): StepDefinition | undefined =>
  STEP_CATALOG.find((definition) => definition.op === op);

/**
 * Turn a form's fields into the step the API expects.
 *
 * Group-by and sort are edited as flat fields because that is how people think
 * about them, then reassembled into the nested shape the backend defines.
 */
export function toApiStep(op: TransformOperation, values: Record<string, unknown>): TransformStep {
  if (op === 'group_by') {
    const aggregation = String(values.metric_aggregation ?? 'sum');
    const column = String(values.metric_column ?? '');
    return {
      op,
      dimensions: (values.dimensions as string[]) ?? [],
      metrics: [
        {
          column,
          aggregation,
          alias: String(values.metric_alias || `${aggregation}_${column}`),
        },
      ],
    };
  }

  if (op === 'sort') {
    return {
      op,
      by: [
        {
          column: String(values.sort_column ?? ''),
          direction: String(values.sort_direction ?? 'asc'),
        },
      ],
    };
  }

  if (op === 'limit') {
    return { op, rows: Number(values.rows ?? 100) };
  }

  const step: TransformStep = { op };
  for (const [key, value] of Object.entries(values)) {
    if (value !== '' && value !== undefined) {
      step[key] = value;
    }
  }
  return step;
}
